import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExportModal = ({ isOpen, onClose, selectedItems, totalItems, allDealerships }) => {
  const [orderDetails, setOrderDetails] = useState({});
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exportScope, setExportScope] = useState('selected');
  const [exportFields, setExportFields] = useState({
    basicInfo: true,
    contactInfo: true,
    addressInfo: true,
    businessInfo: true,
    statistics: true
  });
  const [isExporting, setIsExporting] = useState(false);

  const formats = [
    { value: 'xlsx', label: 'Excel (.xlsx)', icon: 'FileSpreadsheet' },
    { value: 'csv', label: 'CSV (.csv)', icon: 'FileText' },
    { value: 'pdf', label: 'PDF (.pdf)', icon: 'FileText' }
  ];

  const scopes = [
    { 
      value: 'selected', 
      label: `Ausgewählte (${selectedItems?.length})`, 
      disabled: selectedItems?.length === 0 
    },
    { value: 'all', label: `Alle (${totalItems})` },
    { value: 'filtered', label: 'Gefilterte Ergebnisse' }
  ];

  const fieldGroups = [
    {
      key: 'basicInfo',
      label: 'Grundinformationen',
      description: 'Name, Code, ID',
      fields: ['Name', 'Code', 'ID']
    },
    {
      key: 'contactInfo',
      label: 'Kontaktdaten',
      description: 'Ansprechpartner, E-Mail, Telefon',
      fields: ['Ansprechpartner', 'E-Mail', 'Telefon']
    },
    {
      key: 'addressInfo',
      label: 'Adressinformationen',
      description: 'Straße, PLZ, Stadt',
      fields: ['Straße', 'PLZ', 'Stadt']
    },
    {
      key: 'businessInfo',
      label: 'Geschäftsdaten',
      description: 'USt-IdNr., Notizen',
      fields: ['USt-IdNr.', 'Notizen']
    },
    {
      key: 'statistics',
      label: 'Statistiken',
      description: 'Aufträge, Umsatz, Bewertungen',
      fields: ['Aktive Aufträge', 'Abgeschlossene Aufträge', 'Gesamtumsatz', 'Durchschnittliche Bewertung']
    }
  ];
  
  // Add a dedicated statistics export option
  const statisticsExport = {
    key: 'statisticsOnly',
    label: 'Nur Statistiken',
    description: 'Nur Auftrags- und Umsatzdaten exportieren',
    fields: ['Aktive Aufträge', 'Abgeschlossene Aufträge', 'Gesamtumsatz', 'Durchschnittliche Bewertung']
  };

  const handleFieldChange = (field, checked) => {
    setExportFields(prev => ({ ...prev, [field]: checked }));
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      // Get the data to export based on scope
      let dataToExport = [];
      
      if (exportScope === 'selected' && selectedItems?.length > 0) {
        // Get selected dealerships by their IDs
        dataToExport = allDealerships?.filter(dealer => selectedItems.includes(dealer.id)) || [];
      } else if (exportScope === 'all') {
        // Use all dealerships
        dataToExport = allDealerships || [];
      } else {
        // Filtered results - use filtered dealerships
        dataToExport = allDealerships || [];
      }
      
      // Validate that we have data to export
      if (!dataToExport || dataToExport.length === 0) {
        throw new Error('Keine Daten zum Exportieren gefunden. Bitte wählen Sie andere Export-Optionen.');
      }
      
      // If exporting to PDF, fetch detailed order information
      if (exportFormat === 'pdf') {
        await fetchOrderDetails(dataToExport);
      }

      // Filter data based on selected fields
      const filteredData = dataToExport.map(item => {
        const filteredItem = {};
        
        if (exportFields.basicInfo) {
          filteredItem.name = item.name;
          filteredItem.code = item.code;
          filteredItem.id = item.id;
        }
        
        if (exportFields.contactInfo) {
          filteredItem.contactPerson = item.contactPerson;
          filteredItem.email = item.email;
          filteredItem.phone = item.phone;
        }
        
        if (exportFields.addressInfo) {
          filteredItem.street = item.street;
          filteredItem.zipCode = item.zipCode;
          filteredItem.city = item.city;
        }
        
        if (exportFields.businessInfo) {
          filteredItem.vatId = item.vatId;
          filteredItem.notes = item.notes;
        }
        
        if (exportFields.statistics) {
          filteredItem.activeOrders = item.activeOrders;
          filteredItem.completedOrders = item.completedOrders;
          filteredItem.totalRevenue = item.totalRevenue;
          filteredItem.averageRating = item.averageRating;
        }
        
        return filteredItem;
      });

      // Generate and download file based on format
      if (exportFormat === 'csv') {
        await downloadCSV(filteredData);
      } else if (exportFormat === 'xlsx') {
        await downloadExcel(filteredData);
      } else if (exportFormat === 'pdf') {
        await downloadPDF(filteredData);
      }
      
      
      // Show success message
      const formatName = formats.find(f => f.value === exportFormat)?.label || exportFormat;
      alert(`Export erfolgreich abgeschlossen!\n\nFormat: ${formatName}\nAnzahl Einträge: ${filteredData.length}\n\nDie Datei wurde heruntergeladen.`);
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export fehlgeschlagen: ' + error.message);
    } finally {
      setIsExporting(false);
      onClose();
    }
  };

  const downloadCSV = async (data) => {
    if (!data || data.length === 0) return;
    
    // Format headers for better display
    const formatHeader = (header) => {
      const headerMap = {
        'name': 'Name',
        'code': 'Code',
        'id': 'ID',
        'contactPerson': 'Ansprechpartner',
        'email': 'E-Mail',
        'phone': 'Telefon',
        'street': 'Straße',
        'zipCode': 'PLZ',
        'city': 'Stadt',
        'vatId': 'USt-IdNr.',
        'notes': 'Notizen',
        'activeOrders': 'Aktive Aufträge',
        'completedOrders': 'Abgeschlossene Aufträge',
        'totalRevenue': 'Gesamtumsatz (€)',
        'averageRating': 'Durchschnittliche Bewertung'
      };
      return headerMap[header] || header;
    };
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.map(header => formatHeader(header)).join(','),
      ...data.map(row => 
        headers.map(header => {
          let value = row[header] || '';
          
          // Format specific fields
          if (header === 'totalRevenue') {
            value = parseFloat(value || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          } else if (header === 'averageRating') {
            value = value ? parseFloat(value).toFixed(1) : 'Keine Bewertung';
          }
          
          // Escape commas and quotes in CSV
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `autohaus_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadExcel = async (data) => {
    if (!data || data.length === 0) return;
    
    // For Excel, we'll create a CSV-like format that Excel can open
    // In a production app, you'd use a library like xlsx or exceljs
    const formatHeader = (header) => {
      const headerMap = {
        'name': 'Name',
        'code': 'Code',
        'id': 'ID',
        'contactPerson': 'Ansprechpartner',
        'email': 'E-Mail',
        'phone': 'Telefon',
        'street': 'Straße',
        'zipCode': 'PLZ',
        'city': 'Stadt',
        'vatId': 'USt-IdNr.',
        'notes': 'Notizen',
        'activeOrders': 'Aktive Aufträge',
        'completedOrders': 'Abgeschlossene Aufträge',
        'totalRevenue': 'Gesamtumsatz (€)',
        'averageRating': 'Durchschnittliche Bewertung'
      };
      return headerMap[header] || header;
    };
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.map(header => formatHeader(header)).join('\t'),
      ...data.map(row => 
        headers.map(header => {
          let value = row[header] || '';
          
          // Format specific fields
          if (header === 'totalRevenue') {
            value = parseFloat(value || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          } else if (header === 'averageRating') {
            value = value ? parseFloat(value).toFixed(1) : 'Keine Bewertung';
          }
          
          return String(value);
        }).join('\t')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `autohaus_export_${new Date().toISOString().split('T')[0]}.xls`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadPDF = async (data) => {
    if (!data || data.length === 0) return;
    
    // Validate data before PDF generation
    const validationError = validateDataForPDF(data);
    if (validationError) {
      throw new Error(validationError);
    }
    
    try {
      // Import jsPDF dynamically to avoid bundle size issues
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Set font for German characters
      pdf.setFont('helvetica');
      
      let yPosition = 20;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);
      
      // Header
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Autohaus Export', pageWidth / 2, yPosition, { align: 'center' });
      
      yPosition += 15;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('CarHouse Service Manager', pageWidth / 2, yPosition, { align: 'center' });
      
      yPosition += 20;
      
      // Export info
      pdf.setFontSize(10);
      pdf.text(`Exportiert am: ${new Date().toLocaleString('de-DE')}`, margin, yPosition);
      yPosition += 8;
      pdf.text(`Anzahl Autohäuser: ${data.length}`, margin, yPosition);
      yPosition += 15;
      
      // Dealership information table
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Autohaus-Übersicht', margin, yPosition);
      yPosition += 10;
      
      // Table headers
      const headers = Object.keys(data[0]);
      const formatHeader = (header) => {
        const headerMap = {
          'name': 'Name',
          'code': 'Code',
          'id': 'ID',
          'contactPerson': 'Ansprechpartner',
          'email': 'E-Mail',
          'phone': 'Telefon',
          'street': 'Straße',
          'zipCode': 'PLZ',
          'city': 'Stadt',
          'vatId': 'USt-IdNr.',
          'notes': 'Notizen',
          'activeOrders': 'Aktive Aufträge',
          'completedOrders': 'Abgeschlossene Aufträge',
          'totalRevenue': 'Gesamtumsatz (€)',
          'averageRating': 'Durchschnittliche Bewertung'
        };
        return headerMap[header] || header;
      };
      
      // Calculate column widths
      const colWidths = headers.map(header => {
        const headerText = formatHeader(header);
        const textWidth = pdf.getTextWidth(headerText);
        return Math.max(textWidth + 4, 25); // Minimum 25mm width
      });
      
      // Adjust column widths to fit page
      const totalWidth = colWidths.reduce((sum, width) => sum + width, 0);
      if (totalWidth > contentWidth) {
        const scale = contentWidth / totalWidth;
        colWidths.forEach((width, index) => {
          colWidths[index] = width * scale;
        });
      }
      
      // Draw table headers
      let xPosition = margin;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      headers.forEach((header, index) => {
        const headerText = formatHeader(header);
        pdf.rect(xPosition, yPosition - 5, colWidths[index], 8);
        pdf.text(headerText, xPosition + 2, yPosition);
        xPosition += colWidths[index];
      });
      
      yPosition += 8;
      
      // Draw table data
      pdf.setFont('helvetica', 'normal');
      data.forEach((row, rowIndex) => {
        // Check if we need a new page
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
        xPosition = margin;
        headers.forEach((header, colIndex) => {
          let value = row[header] || '';
          
          // Format specific fields safely
          if (header === 'totalRevenue') {
            value = `€ ${parseFloat(value || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          } else if (header === 'averageRating') {
            value = value ? `${parseFloat(value).toFixed(1)}/5` : 'Keine Bewertung';
          } else if (header === 'activeOrders' || header === 'completedOrders') {
            value = parseInt(value) || 0;
          } else if (header === 'notes' && value && value.length > 30) {
            value = value.substring(0, 30) + '...';
          }
          
          // Ensure value is a string and handle null/undefined
          value = String(value || '');
          
          // Truncate text if too long
          const maxLength = Math.floor(colWidths[colIndex] / 2); // Rough estimate
          if (value.length > maxLength) {
            value = value.substring(0, maxLength - 3) + '...';
          }
          
          pdf.rect(xPosition, yPosition - 5, colWidths[colIndex], 8);
          pdf.text(value, xPosition + 2, yPosition);
          xPosition += colWidths[colIndex];
        });
        
        yPosition += 8;
      });
      
      yPosition += 15;
      
             // Now add detailed order information for each dealership
       pdf.setFontSize(14);
       pdf.setFont('helvetica', 'bold');
       pdf.text('Detaillierte Auftrags-Informationen', margin, yPosition);
       yPosition += 15;
       
       // For each dealership, add their orders
       for (const dealership of data) {
         // Check if we need a new page
         if (yPosition > 250) {
           pdf.addPage();
           yPosition = 20;
         }
         
         pdf.setFontSize(12);
         pdf.setFont('helvetica', 'bold');
         pdf.text(`${dealership.name} (${dealership.code})`, margin, yPosition);
         yPosition += 8;
         
         pdf.setFontSize(10);
         pdf.setFont('helvetica', 'normal');
         pdf.text(`Adresse: ${dealership.street}, ${dealership.zipCode} ${dealership.city}`, margin, yPosition);
         yPosition += 6;
         pdf.text(`Kontakt: ${dealership.contactPerson} | ${dealership.email} | ${dealership.phone}`, margin, yPosition);
         yPosition += 8;
         
         // Add order statistics
         pdf.text(`Aktive Aufträge: ${dealership.activeOrders || 0}`, margin, yPosition);
         yPosition += 5;
         pdf.text(`Abgeschlossene Aufträge: ${dealership.completedOrders || 0}`, margin, yPosition);
         yPosition += 5;
         pdf.text(`Gesamtumsatz: € ${parseFloat(dealership.totalRevenue || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
         yPosition += 5;
         pdf.text(`Durchschnittliche Bewertung: ${dealership.averageRating ? parseFloat(dealership.averageRating).toFixed(1) + '/5' : 'Keine Bewertung'}`, margin, yPosition);
         yPosition += 10;
         
         // Add detailed order information
         const dealershipOrders = orderDetails[dealership.id] || [];
         if (dealershipOrders.length > 0) {
           pdf.setFontSize(11);
           pdf.setFont('helvetica', 'bold');
           pdf.text('Aufträge:', margin, yPosition);
           yPosition += 8;
           
           for (const order of dealershipOrders.slice(0, 10)) { // Limit to 10 orders to avoid PDF overflow
             // Check if we need a new page
             if (yPosition > 250) {
               pdf.addPage();
               yPosition = 20;
             }
             
             pdf.setFontSize(9);
             pdf.setFont('helvetica', 'bold');
             pdf.text(`Auftrag #${order.order_number} - ${order.status}`, margin, yPosition);
             yPosition += 5;
             
             pdf.setFont('helvetica', 'normal');
             pdf.text(`Kunde: ${order.customer_name || 'N/A'} | Titel: ${order.title || 'N/A'}`, margin, yPosition);
             yPosition += 4;
             
             if (order.vehicle) {
               pdf.text(`Fahrzeug: ${order.vehicle.year} ${order.vehicle.make} ${order.vehicle.model} (${order.vehicle.color}) - VIN: ${order.vehicle.vin}`, margin, yPosition);
               yPosition += 4;
             }
             
             pdf.text(`Erstellt: ${new Date(order.created_at).toLocaleDateString('de-DE')} | Betrag: € ${parseFloat(order.total_amount || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPosition);
             yPosition += 4;
             
             if (order.rating) {
               pdf.text(`Bewertung: ${order.rating}/5`, margin, yPosition);
               yPosition += 4;
             }
             
             if (order.notes) {
               pdf.text(`Notizen: ${order.notes.substring(0, 100)}${order.notes.length > 100 ? '...' : ''}`, margin, yPosition);
               yPosition += 4;
             }
             
             // Add service items if available
             if (order.order_service_items && order.order_service_items.length > 0) {
               pdf.text('Service-Items:', margin, yPosition);
               yPosition += 4;
               
               for (const item of order.order_service_items.slice(0, 3)) { // Limit to 3 items
                 const serviceName = item.service_item?.name || 'Unbekannter Service';
                 const quantity = item.quantity || 1;
                 const price = item.unit_price || 0;
                 pdf.text(`  • ${serviceName} (${quantity}x) - € ${parseFloat(price).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin + 5, yPosition);
                 yPosition += 3;
               }
               
               if (order.order_service_items.length > 3) {
                 pdf.text(`  ... und ${order.order_service_items.length - 3} weitere`, margin + 5, yPosition);
                 yPosition += 3;
               }
             }
             
             yPosition += 5;
           }
           
           if (dealershipOrders.length > 10) {
             pdf.text(`... und ${dealershipOrders.length - 10} weitere Aufträge`, margin, yPosition);
             yPosition += 5;
           }
         } else {
           pdf.text('Keine Aufträge verfügbar', margin, yPosition);
           yPosition += 5;
         }
         
         // Add a separator line
         pdf.line(margin, yPosition, pageWidth - margin, yPosition);
         yPosition += 15;
       }
      
      // Footer
      const totalPages = pdf.internal.getNumberOfPages();
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        pdf.setPage(pageNum);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Seite ${pageNum} von ${totalPages}`, pageWidth / 2, 290, { align: 'center' });
        pdf.text('Generiert von CarHouse Service Manager', pageWidth / 2, 295, { align: 'center' });
      }
      
      // Save the PDF
      pdf.save(`autohaus_export_${new Date().toISOString().split('T')[0]}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      
      // Provide more specific error messages
      let errorMessage = 'PDF-Generierung fehlgeschlagen.';
      
      if (error.message.includes('jspdf')) {
        errorMessage += ' jsPDF Bibliothek konnte nicht geladen werden.';
      } else if (error.message.includes('font')) {
        errorMessage += ' Schriftart-Problem aufgetreten.';
      } else if (error.message.includes('data')) {
        errorMessage += ' Datenformatierungsproblem aufgetreten.';
      } else {
        errorMessage += ' Unbekannter Fehler: ' + error.message;
      }
      
      // Fallback to HTML if PDF generation fails
      alert(errorMessage + '\n\nVerwende HTML-Export als Fallback.');
      await downloadPDFFallback(data);
    }
  };
  
  const downloadPDFFallback = async (data) => {
    if (!data || data.length === 0) return;
    
    // Fallback HTML export
    const headers = Object.keys(data[0]);
    
    const formatHeader = (header) => {
      const headerMap = {
        'name': 'Name',
        'code': 'Code',
        'id': 'ID',
        'contactPerson': 'Ansprechpartner',
        'email': 'E-Mail',
        'phone': 'Telefon',
        'street': 'Straße',
        'zipCode': 'PLZ',
        'city': 'Stadt',
        'vatId': 'USt-IdNr.',
        'notes': 'Notizen',
        'activeOrders': 'Aktive Aufträge',
        'completedOrders': 'Abgeschlossene Aufträge',
        'totalRevenue': 'Gesamtumsatz (€)',
        'averageRating': 'Durchschnittliche Bewertung'
      };
      return headerMap[header] || header;
    };
    
    let htmlContent = `
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              color: #333;
              line-height: 1.6;
            }
            .header { 
              text-align: center; 
              border-bottom: 2px solid #333; 
              padding-bottom: 20px; 
              margin-bottom: 30px;
            }
            h1 { 
              color: #333; 
              margin: 0 0 10px 0;
              font-size: 28px;
            }
            .export-info {
              background: #f8f9fa;
              padding: 15px;
              border-radius: 5px;
              margin-bottom: 20px;
            }
            table { 
              border-collapse: collapse; 
              width: 100%; 
              margin-top: 20px;
              font-size: 12px;
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 10px 8px; 
              text-align: left; 
              vertical-align: top;
            }
            th { 
              background-color: #f2f2f2; 
              font-weight: bold;
              color: #333;
            }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .dealership-section {
              margin-top: 30px;
              padding: 20px;
              border: 1px solid #ddd;
              border-radius: 5px;
              background: #f9f9f9;
            }
            .dealership-header {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 15px;
              color: #333;
            }
            .order-stats {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;
              margin: 15px 0;
            }
            .stat-item {
              background: white;
              padding: 10px;
              border-radius: 3px;
              border: 1px solid #ddd;
            }
            .stat-label {
              font-weight: bold;
              color: #666;
              font-size: 12px;
            }
            .stat-value {
              font-size: 16px;
              color: #333;
              margin-top: 5px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Autohaus Export</h1>
            <p>CarHouse Service Manager</p>
          </div>
          
          <div class="export-info">
            <p><strong>Exportiert am:</strong> ${new Date().toLocaleString('de-DE')}</p>
            <p><strong>Anzahl Einträge:</strong> ${data.length}</p>
            <p><strong>Export Format:</strong> HTML (PDF-Fallback)</p>
          </div>
          
          <table>
            <thead>
              <tr>
                ${headers.map(header => `<th>${formatHeader(header)}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${data.map(row => 
                `<tr>${headers.map(header => {
                  let value = row[header] || '';
                  if (header === 'totalRevenue') {
                    value = `€ ${parseFloat(value || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                  } else if (header === 'averageRating') {
                    value = value ? `${parseFloat(value).toFixed(1)}/5` : 'Keine Bewertung';
                  } else if (header === 'notes' && value.length > 50) {
                    value = value.substring(0, 50) + '...';
                  }
                  return `<td>${value}</td>`;
                }).join('')}</tr>`
              ).join('')}
            </tbody>
          </table>
          
                     ${data.map(dealership => {
             const dealershipOrders = orderDetails[dealership.id] || [];
             return `
               <div class="dealership-section">
                 <div class="dealership-header">${dealership.name} (${dealership.code})</div>
                 <p><strong>Adresse:</strong> ${dealership.street}, ${dealership.zipCode} ${dealership.city}</p>
                 <p><strong>Kontakt:</strong> ${dealership.contactPerson} | ${dealership.email} | ${dealership.phone}</p>
                 <div class="order-stats">
                   <div class="stat-item">
                     <div class="stat-label">Aktive Aufträge</div>
                     <div class="stat-value">${dealership.activeOrders || 0}</div>
                   </div>
                   <div class="stat-item">
                     <div class="stat-label">Abgeschlossene Aufträge</div>
                     <div class="stat-value">${dealership.completedOrders || 0}</div>
                   </div>
                   <div class="stat-item">
                     <div class="stat-label">Gesamtumsatz</div>
                     <div class="stat-value">€ ${parseFloat(dealership.totalRevenue || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                   </div>
                   <div class="stat-item">
                     <div class="stat-label">Durchschnittliche Bewertung</div>
                     <div class="stat-value">${dealership.averageRating ? parseFloat(dealership.averageRating).toFixed(1) + '/5' : 'Keine Bewertung'}</div>
                   </div>
                 </div>
                 
                 ${dealershipOrders.length > 0 ? `
                   <div class="orders-detail" style="margin-top: 20px;">
                     <h4 style="margin: 15px 0 10px 0; color: #333;">Aufträge (${dealershipOrders.length})</h4>
                     ${dealershipOrders.slice(0, 5).map(order => `
                       <div class="order-item" style="background: white; padding: 15px; margin: 10px 0; border-radius: 5px; border: 1px solid #ddd;">
                         <div style="font-weight: bold; color: #333; margin-bottom: 8px;">
                           Auftrag #${order.order_number} - ${order.status}
                         </div>
                         <div style="font-size: 14px; color: #666; line-height: 1.4;">
                           <p><strong>Kunde:</strong> ${order.customer_name || 'N/A'} | <strong>Titel:</strong> ${order.title || 'N/A'}</p>
                           ${order.vehicle ? `<p><strong>Fahrzeug:</strong> ${order.vehicle.year} ${order.vehicle.make} ${order.vehicle.model} (${order.vehicle.color}) - VIN: ${order.vehicle.vin}</p>` : ''}
                           <p><strong>Erstellt:</strong> ${new Date(order.created_at).toLocaleDateString('de-DE')} | <strong>Betrag:</strong> € ${parseFloat(order.total_amount || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                           ${order.rating ? `<p><strong>Bewertung:</strong> ${order.rating}/5</p>` : ''}
                           ${order.notes ? `<p><strong>Notizen:</strong> ${order.notes.substring(0, 150)}${order.notes.length > 150 ? '...' : ''}</p>` : ''}
                           
                           ${order.order_service_items && order.order_service_items.length > 0 ? `
                             <div style="margin-top: 10px;">
                               <strong>Service-Items:</strong>
                               <ul style="margin: 5px 0 0 20px; padding: 0;">
                                 ${order.order_service_items.slice(0, 3).map(item => {
                                   const serviceName = item.service_item?.name || 'Unbekannter Service';
                                   const quantity = item.quantity || 1;
                                   const price = item.unit_price || 0;
                                   return `<li>${serviceName} (${quantity}x) - € ${parseFloat(price).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</li>`;
                                 }).join('')}
                                 ${order.order_service_items.length > 3 ? `<li>... und ${order.order_service_items.length - 3} weitere</li>` : ''}
                               </ul>
                             </div>
                           ` : ''}
                         </div>
                       </div>
                     `).join('')}
                     ${dealershipOrders.length > 5 ? `<p style="text-align: center; color: #666; font-style: italic;">... und ${dealershipOrders.length - 5} weitere Aufträge</p>` : ''}
                   </div>
                 ` : '<p style="text-align: center; color: #666; font-style: italic; margin-top: 20px;">Keine Aufträge verfügbar</p>'}
               </div>
             `;
           }).join('')}
          
          <div style="margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 20px;">
            <p>Generiert von CarHouse Service Manager</p>
            <p>HTML-Export (PDF-Fallback)</p>
          </div>
        </body>
      </html>
    `;
    
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `autohaus_export_${new Date().toISOString().split('T')[0]}.html`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getSelectedFieldsCount = () => {
    return Object.values(exportFields)?.filter(Boolean)?.length;
  };

  const validateDataForPDF = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return 'Keine Daten zum Exportieren verfügbar.';
    }
    
    // Check if first row has required structure
    const firstRow = data[0];
    if (!firstRow || typeof firstRow !== 'object') {
      return 'Datenformat ist ungültig.';
    }
    
    // Validate that all required fields are present and have valid values
    for (const row of data) {
      // Check for null/undefined values that might cause PDF generation issues
      for (const [key, value] of Object.entries(row)) {
        if (value === null || value === undefined) {
          console.warn(`Warning: Field ${key} has null/undefined value in row:`, row);
        }
        
        // Ensure numeric fields are actually numbers
        if (['activeOrders', 'completedOrders', 'totalRevenue', 'averageRating'].includes(key)) {
          if (value !== null && value !== undefined && isNaN(parseFloat(value))) {
            console.warn(`Warning: Field ${key} has non-numeric value: ${value}`);
          }
        }
      }
    }
    
    return null; // No validation errors
  };

  const fetchOrderDetails = async (dealerships) => {
    setIsLoadingOrders(true);
    try {
      const { supabase } = await import('../../../lib/supabase');
      
      const orderData = {};
      
      for (const dealership of dealerships) {
        // Fetch orders for this dealership
        const { data: orders, error } = await supabase
          .from('orders')
          .select(`
            id,
            order_number,
            status,
            total_amount,
            customer_name,
            title,
            created_at,
            completed_at,
            rating,
            notes,
            vehicle:vehicles(
              vin,
              make,
              model,
              year,
              color
            ),
            order_service_items(
              quantity,
              unit_price,
              total_price,
              service_item:service_items(
                name,
                base_price,
                service_type:service_types(name, price)
              )
            )
          `)
          .eq('car_house_id', dealership.id)
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error(`Error fetching orders for dealership ${dealership.id}:`, error);
          orderData[dealership.id] = [];
        } else {
          orderData[dealership.id] = orders || [];
        }
      }
      
      setOrderDetails(orderData);
      
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-1200 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
              <Icon name="Download" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Daten exportieren</h2>
              <p className="text-sm text-muted-foreground">
                Autohaus-Daten in verschiedenen Formaten exportieren
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Export Format */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-3">Format auswählen</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {formats?.map((format) => (
                <button
                  key={format?.value}
                  onClick={() => setExportFormat(format?.value)}
                  className={`flex items-center space-x-3 p-4 rounded-lg border transition-colors duration-150 ${
                    exportFormat === format?.value
                      ? 'border-primary bg-primary/5 text-primary' :'border-border hover:bg-muted'
                  }`}
                >
                  <Icon name={format?.icon} size={20} />
                  <span className="font-medium">{format?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Export Scope */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-3">Umfang auswählen</h3>
            <div className="space-y-2">
              {scopes?.map((scope) => (
                <button
                  key={scope?.value}
                  onClick={() => !scope?.disabled && setExportScope(scope?.value)}
                  disabled={scope?.disabled}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors duration-150 ${
                    scope?.disabled
                      ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                      : exportScope === scope?.value
                      ? 'border-primary bg-primary/5 text-primary' :'border-border hover:bg-muted'
                  }`}
                >
                  <span className="font-medium">{scope?.label}</span>
                  {exportScope === scope?.value && (
                    <Icon name="Check" size={16} />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Export Fields */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-foreground">Felder auswählen</h3>
              <span className="text-sm text-muted-foreground">
                {getSelectedFieldsCount()} von {fieldGroups?.length} Gruppen ausgewählt
              </span>
            </div>
            <div className="space-y-3">
              {fieldGroups?.map((group) => (
                <div
                  key={group?.key}
                  className={`p-4 rounded-lg border transition-colors duration-150 ${
                    exportFields?.[group?.key]
                      ? 'border-primary bg-primary/5' :'border-border'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={exportFields?.[group?.key]}
                      onChange={(e) => handleFieldChange(group?.key, e?.target?.checked)}
                      className="mt-1 rounded border-border"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-foreground">{group?.label}</div>
                      <div className="text-sm text-muted-foreground mb-2">{group?.description}</div>
                      <div className="flex flex-wrap gap-1">
                        {group?.fields?.map((field) => (
                          <span
                            key={field}
                            className="inline-flex items-center px-2 py-1 rounded text-xs bg-muted text-muted-foreground"
                          >
                            {field}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

                     {/* Quick Export Options */}
           <div>
             <h3 className="text-lg font-medium text-foreground mb-3">Schnell-Export</h3>
             <div className="space-y-2">
               <button
                 onClick={() => {
                   setExportFields({
                     basicInfo: false,
                     contactInfo: false,
                     addressInfo: false,
                     businessInfo: false,
                     statistics: true
                   });
                 }}
                 className="w-full flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted transition-colors duration-150"
               >
                 <div>
                   <div className="font-medium text-foreground">Statistiken exportieren</div>
                   <div className="text-sm text-muted-foreground">Nur Auftrags- und Umsatzdaten</div>
                 </div>
                 <Icon name="BarChart3" size={20} className="text-primary" />
               </button>
               <button
                 onClick={() => {
                   setExportFields({
                     basicInfo: true,
                     contactInfo: true,
                     addressInfo: true,
                     businessInfo: true,
                     statistics: true
                   });
                 }}
                 className="w-full flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted transition-colors duration-150"
               >
                 <div>
                   <div className="font-medium text-foreground">Alles exportieren</div>
                   <div className="text-sm text-muted-foreground">Alle verfügbaren Daten</div>
                 </div>
                 <Icon name="Download" size={20} className="text-primary" />
               </button>
             </div>
           </div>

           {/* Export Preview */}
           <div className="bg-muted rounded-lg p-4">
             <h4 className="font-medium text-foreground mb-2">Export-Vorschau</h4>
             <div className="text-sm text-muted-foreground space-y-1">
               <div>Format: <span className="font-medium">{formats?.find(f => f?.value === exportFormat)?.label}</span></div>
               <div>Umfang: <span className="font-medium">{scopes?.find(s => s?.value === exportScope)?.label}</span></div>
               <div>Felder: <span className="font-medium">{getSelectedFieldsCount()} Gruppen</span></div>
               <div>Geschätzte Dateigröße: <span className="font-medium">~2.5 MB</span></div>
             </div>
           </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border flex-shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isExporting}
          >
            Abbrechen
          </Button>
          <Button
            variant="default"
            onClick={handleExport}
            loading={isExporting || isLoadingOrders}
            disabled={getSelectedFieldsCount() === 0}
          >
            <Icon name="Download" size={16} className="mr-2" />
            {isExporting ? 'Exportiere...' : isLoadingOrders ? 'Lade Aufträge...' : 'Exportieren'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;